import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Seo from '../components/Seo';
import { Phone, Waves, ShieldCheck, Grid, X } from 'lucide-react';
import { CONTACT } from '../data/constants';
import {
  PDC_SPAS_CATEGORIES,
  PDC_SPAS_PRODUCTS,
  PDC_SPAS_HERO,
  PDC_SPAS_LIFESTYLE,
} from '../data/pdcSpas';
import { PDCProductCard, phoneHref } from '../components/pdcSpaParts';

const PDCSpasPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  // Full Midnight Series photo set (scraped from PDC's Midnight gallery).
  const MID = 'https://cdn.cmsmax.cloud/production/width=1280,quality=70,format=webp/qq67leqlk45sgt0rapx2j/';
  const MIDNIGHT_GALLERY = [
    'midnight-biscayne.png', 'midnight-shell.jpg', 'midnight-detail.jpg', 'midnight-cabinet.jpg',
    'midnight-control-panel.jpg', 'midnight-luminx.jpg', 'midnight-sereis-luminx-light.jpg',
    'midnight-sereis-spectrum-lighting.jpg', 'midnight-sereis-fiji-hot-tub.jpg', 'midnight-series-luxury.jpg',
    'midnight-splash.jpg', 'midnight-series-splash-15.jpg', 'midnight-splash-install.png',
    'midnight-sereis-splashpak.jpg', 'midnight-solace-install.png', 'midnight-biscayne-install-1.png',
    'splash-spectrum-lights.jpg',
  ].map((f) => MID + f);

  // Midnight Series = limited-edition black-acrylic finish offered across existing
  // models. Each card links to that model's own detail page (full specs + gallery).
  const MIDNIGHT_GROUPS = [
    { id: 'hot-tubs', label: 'Midnight Hot Tubs', blurb: 'Bold black acrylic shell meets full-body luxury hydrotherapy.', ids: ['pdc-biscayne', 'pdc-bali', 'pdc-reno'] },
    { id: 'splash-spas', label: 'Midnight Splash Spas', blurb: "The 12\u2032 & 15\u2032 pool-alternative, dressed for the dark.", ids: ['pdc-splash12', 'pdc-splash15'] },
    { id: 'solace', label: 'Midnight Solace Cold Plunges', blurb: 'Compact recovery retreats with the exclusive LUMINX\u2122 lighting.', ids: ['pdc-xd1', 'pdc-xd1plus', 'pdc-xd2'] },
  ];
  const productById = (id) => PDC_SPAS_PRODUCTS.find((p) => p.id === id);

  const allCategories = [
    { id: 'all', name: 'All Products', count: PDC_SPAS_PRODUCTS.length },
    ...PDC_SPAS_CATEGORIES.map((c) => ({
      id: c.id,
      name: c.name,
      count: PDC_SPAS_PRODUCTS.filter((p) => p.category === c.id).length,
    })),
  ];

  const filtered = useMemo(() => (
    activeCategory === 'all'
      ? PDC_SPAS_PRODUCTS
      : PDC_SPAS_PRODUCTS.filter((p) => p.category === activeCategory)
  ), [activeCategory]);

  return (
    <>
      <Seo page="/pdc-spas" />

      <div className="pt-28 md:pt-36 pb-20" style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #EAF2FF 25%, #D6E6FF 55%, #EAF2FF 85%, #ffffff 100%)'
      }} data-testid="pdc-spas-page">
        {/* Hero */}
        <section className="relative h-[55vh] min-h-[400px] flex items-center justify-center overflow-hidden mb-12 md:mb-16">
          <div className="absolute inset-0">
            <img src={PDC_SPAS_HERO} alt="PDC Spas hot tub" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0E3D95]/75 via-[#0E3D95]/45 to-[#0E3D95]/85" />
          </div>
          <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-center gap-2 mb-4">
              <Waves className="text-[#FFD700]" size={24} />
              <span className="text-sm uppercase tracking-widest text-[#FFD700] font-semibold">Authorized Naples, FL Dealer</span>
              <Waves className="text-[#FFD700]" size={24} />
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold uppercase mb-5 leading-none">
              PDC <span className="text-[#F88200]">Spas</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="text-lg md:text-2xl text-slate-100 max-w-3xl mx-auto mb-6">
              68 years of American-made luxury. Explore hot tubs, award-winning swim spas, and the X-Degree cold plunge — each built to order in your choice of colors.
            </motion.p>
            <motion.a href={phoneHref} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
              className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4">
              <Phone size={20} /> Call for Pricing · {CONTACT.phone}
            </motion.a>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Highlights */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-4 mb-12 md:mb-16">
            {[
              { title: 'American Made', text: 'Proudly crafted in Pennsylvania, USA for over six decades — built to order, just for you.' },
              { title: 'Industry-Leading Warranty', text: 'Up to a 35-year structural warranty across the Luxury Series and swim spa lines.' },
              { title: 'Built Your Way', text: 'Choose from designer acrylic shell colors and durable, maintenance-free cabinet finishes.' },
            ].map((item) => (
              <div key={item.title} className="bg-white/80 backdrop-blur-sm border border-blue-200 rounded-xl p-5 shadow-sm">
                <div className="w-12 h-12 bg-[#F88200] rounded-full flex items-center justify-center mb-3">
                  <ShieldCheck className="text-white" size={20} />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0E3D95] mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.text}</p>
              </div>
            ))}
          </motion.div>

          {/* NEW Limited Edition — Midnight Series */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-2xl mb-12 md:mb-16 shadow-2xl"
            style={{ background: 'linear-gradient(135deg, #0B0F1A 0%, #141A2B 50%, #0B0F1A 100%)' }}
            data-testid="pdc-midnight-series"
          >
            <div className="absolute inset-0 opacity-30">
              <img
                src="https://cdn.cmsmax.cloud/production/width=1280,quality=70,format=webp/qq67leqlk45sgt0rapx2j/midnight-biscayne-install-1_1.png"
                alt="PDC Midnight Series hot tub at night"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10 p-8 md:p-12">
              <div className="text-center max-w-3xl mx-auto mb-10">
                <span className="inline-block bg-[#F88200] text-white text-xs md:text-sm font-bold uppercase tracking-widest px-4 py-1 rounded-full mb-4">
                  New Limited Edition
                </span>
                <h2 className="font-['Barlow_Condensed'] text-4xl md:text-6xl font-black uppercase text-white leading-none mb-4">
                  Midnight <span className="text-[#F88200]">Series</span>
                </h2>
                <p className="text-lg md:text-xl text-slate-200 mb-2">Bold black acrylic. Luxury after dark.</p>
                <p className="text-slate-300">
                  A limited-edition collection featuring a sleek black acrylic shell and the exclusive LUMINX™ lighting — designed to turn every evening into something extraordinary.
                </p>
              </div>

              <div className="mb-12" data-testid="pdc-midnight-gallery">
                <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase text-white text-center mb-1">
                  Explore The Midnight Collection
                </h3>
                <p className="text-slate-300 text-sm text-center mb-6">Every detail of the Midnight finish — shell, cabinet, LUMINX™ lighting, controls and installs. Tap any photo to enlarge.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {MIDNIGHT_GALLERY.map((src, i) => (
                    <button
                      type="button"
                      key={src}
                      onClick={() => setLightbox(i)}
                      className="group relative aspect-square overflow-hidden rounded-lg bg-black/40 border border-white/10 hover:border-[#F88200] transition-colors"
                      data-testid={`midnight-photo-${i}`}
                    >
                      <img src={src} alt={`PDC Midnight Series ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-10 mb-10" data-testid="pdc-midnight-groups">
                {MIDNIGHT_GROUPS.map((group) => (
                  <div key={group.id} data-testid={`midnight-group-${group.id}`}>
                    <div className="mb-4">
                      <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase text-white leading-none">{group.label}</h3>
                      <p className="text-slate-300 text-sm md:text-base mt-1">{group.blurb}</p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {group.ids.map(productById).filter(Boolean).map((p) => (
                        <PDCProductCard key={p.id} product={p} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact" className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4">
                  Request Information
                </Link>
                <a href={phoneHref} className="border-2 border-white text-white hover:bg-white/10 px-6 py-3 font-['Barlow_Condensed'] font-bold uppercase tracking-wider transition-colors rounded inline-flex items-center gap-2">
                  <Phone size={18} /> {CONTACT.phone}
                </a>
              </div>
              <p className="text-center text-slate-400 text-sm mt-6">
                Limited production. Uncompromising design. Contact us to reserve yours.
              </p>
            </div>
          </motion.section>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 md:gap-3 mb-6">
            {allCategories.map((cat) => (
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 md:px-5 md:py-3 font-semibold text-sm md:text-base transition-all rounded-lg ${
                  activeCategory === cat.id ? 'bg-[#0E3D95] text-white shadow-lg' : 'bg-white/80 text-[#0E3D95] hover:bg-[#F88200] hover:text-white border border-slate-200'
                }`}
                data-testid={`pdc-category-btn-${cat.id}`}>
                {cat.id === 'all' && <Grid size={16} />}
                <span>{cat.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${activeCategory === cat.id ? 'bg-white/20' : 'bg-slate-100'}`}>{cat.count}</span>
              </button>
            ))}
          </div>

          {/* Category description */}
          {activeCategory !== 'all' && (
            <p className="text-slate-600 max-w-3xl mb-6">
              {PDC_SPAS_CATEGORIES.find((c) => c.id === activeCategory)?.description}
            </p>
          )}

          {/* Product Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {filtered.map((p) => (
              <PDCProductCard key={p.id} product={p} />
            ))}
          </div>

          {/* Lifestyle / CTA */}
          <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-xl mb-12">
            <div className="aspect-[4/3] md:aspect-auto">
              <img src={PDC_SPAS_LIFESTYLE} alt="PDC Spas swim spa lifestyle" className="w-full h-full object-cover" />
            </div>
            <div className="bg-gradient-to-br from-[#0E3D95] to-[#1a4d8a] text-white p-8 md:p-12 flex flex-col justify-center">
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold uppercase leading-none mb-4">
                Your Year-Round <span className="text-[#F88200]">Home Oasis</span>
              </h2>
              <p className="text-white/90 leading-relaxed mb-6">
                From full-body hydrotherapy hot tubs to award-winning swim spas and the X-Degree cold plunge, PDC Spas sets the standard for American-made wellness. Visit us in Naples to find your perfect fit — and we&apos;ll walk you through every color and option.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href={phoneHref} className="btn-primary inline-flex items-center gap-2">
                  <Phone size={18} /> Call for Pricing
                </a>
                <Link to="/contact" className="border-2 border-white text-white hover:bg-white/10 px-6 py-3 font-['Barlow_Condensed'] font-bold uppercase tracking-wider transition-colors rounded">
                  Request a Quote
                </Link>
              </div>
            </div>
          </motion.section>

          {/* Dealer note */}
          <div className="text-center text-sm text-slate-500 max-w-3xl mx-auto">
            <p className="italic">
              Leisure Wellness Naples is an authorized PDC Spas dealer serving Naples and Southwest Florida.
              All models are built to order — call us for current pricing, delivery, and installation.
            </p>
          </div>
        </div>
      </div>

      {/* Midnight gallery lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="midnight-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 rounded-full p-2"
              aria-label="Close"
              data-testid="midnight-lightbox-close"
            >
              <X size={24} className="text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + MIDNIGHT_GALLERY.length) % MIDNIGHT_GALLERY.length); }}
              className="absolute left-3 md:left-6 text-white/70 hover:text-white text-4xl font-light px-3"
              aria-label="Previous"
            >‹</button>
            <motion.img
              key={lightbox}
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              src={MIDNIGHT_GALLERY[lightbox]}
              alt={`PDC Midnight Series ${lightbox + 1}`}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % MIDNIGHT_GALLERY.length); }}
              className="absolute right-3 md:right-6 text-white/70 hover:text-white text-4xl font-light px-3"
              aria-label="Next"
            >›</button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PDCSpasPage;
